import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useHistory, useParams } from 'react-router-dom';
import { Alert, Badge, Button, Input, Modal, Popconfirm, Radio, Space, Spin, Tag, Tooltip, Typography, message } from 'antd';
import { ArrowLeftOutlined, CheckCircleFilled, DeleteOutlined, EyeInvisibleOutlined, EyeOutlined, ReloadOutlined, StopOutlined } from '@ant-design/icons';
import { ingestionStore } from './store/IngestionStore';
import { ingestionApi } from './ingestionApi';
import { MODEL_ITEM_TYPES, PIPELINE_STEPS, STATUS_LABELS } from './itemTypes';
import { loadStepReview, saveStepReview } from './stepReview';
import { Step1Variations } from './steps/Step1Variations';
import { Step2Scrape } from './steps/Step2Scrape';
import { Step3Extract } from './steps/Step3Extract';
import { Step4Assets } from './steps/Step4Assets';
import { Step5Compress } from './steps/Step5Compress';
import { Step6Confirm } from './steps/Step6Confirm';
import { useCurrentUser } from '../../user/permissions';
import { PlaceholderStep } from './steps/PlaceholderStep';

const { Title } = Typography;

// Canned reasons for turning down a user request; every option is shown to
// the submitter verbatim (notification + their request list), so keep them
// blame-free and next-step oriented like the moderation-check copy.
const REJECT_PRESETS = [
    { key: 'exists', label: 'Item already exists', reason: 'This item is already in the catalog.' },
    { key: 'category', label: "Doesn't fit an existing category", reason: "The item you asked for doesn't fit in any of our existing categories." },
    {
        key: 'site',
        label: 'Unsupported website',
        reason: "We can only add items from the retailers and brands we work with, and this website isn't one of them yet.",
    },
    {
        key: 'notForSale',
        label: 'Not an item for sale (auction, listing, etc.)',
        reason: "The link you shared isn't an item for sale. We only add items from real online retailers, not auctions or other listings.",
    },
    { key: 'other', label: 'Other (free text)', reason: '' },
];

const RejectRequestDialog = ({ open, submitting, onCancel, onConfirm }) => {
    const [preset, setPreset] = useState(REJECT_PRESETS[0].key);
    const [custom, setCustom] = useState('');
    const isOther = preset === 'other';
    const reason = isOther ? custom.trim() : REJECT_PRESETS.find((p) => p.key === preset).reason;
    return (
        <Modal
            title="Reject this request?"
            open={open}
            onCancel={onCancel}
            onOk={() => onConfirm(reason)}
            okText="Reject request"
            okButtonProps={{ danger: true, disabled: !reason, loading: submitting }}
            destroyOnClose
        >
            <Typography.Paragraph type="secondary">
                The request is closed for good and the user gets a notification with the reason below,
                followed by “Disagree? Contact us at info@designwithfriends.com or on Instagram.”
            </Typography.Paragraph>
            <Radio.Group
                value={preset}
                onChange={(e) => setPreset(e.target.value)}
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
            >
                {REJECT_PRESETS.map((p) => (
                    <Radio key={p.key} value={p.key}>
                        {p.label}
                        {p.reason && (
                            <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                                “{p.reason}”
                            </Typography.Text>
                        )}
                    </Radio>
                ))}
            </Radio.Group>
            {isOther && (
                <Input.TextArea
                    autoFocus
                    rows={3}
                    maxLength={500}
                    showCount
                    style={{ marginTop: 12 }}
                    placeholder="Tell the user why, in one or two sentences"
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                />
            )}
        </Modal>
    );
};

const DoneBadge = () => (
    <span
        style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            color: '#73d13d',
            fontSize: 12,
            fontWeight: 500,
        }}
    >
        <CheckCircleFilled style={{ fontSize: 12 }} />
        Done
    </span>
);

const CurrentBadge = () => (
    <span
        style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '1px 10px',
            borderRadius: 11,
            background: 'rgba(22,119,255,0.16)',
            color: '#3c9aff',
            fontSize: 12,
            fontWeight: 600,
            lineHeight: '18px',
        }}
    >
        <Badge status="processing" />
        Current
    </span>
);

const UnseenChip = () => (
    <span
        style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '1px 10px',
            borderRadius: 11,
            fontSize: 12,
            fontWeight: 500,
            lineHeight: '18px',
            background: 'rgba(255,255,255,0.08)',
            color: 'rgba(255,255,255,0.50)',
        }}
    >
        <EyeInvisibleOutlined style={{ fontSize: 12 }} />
        Unseen
    </span>
);

// Translucent tints over the app's dark background (see routerOutlet darkAlgorithm).
const STEP_COLORS = {
    done: { bg: 'rgba(82,196,26,0.10)', bgViewed: 'rgba(82,196,26,0.28)', title: '#73d13d' },
    current: { bg: 'rgba(22,119,255,0.14)', bgViewed: 'rgba(22,119,255,0.34)', title: '#69b1ff' },
    future: { bg: 'rgba(255,255,255,0.05)', bgViewed: 'rgba(255,255,255,0.16)', title: 'rgba(255,255,255,0.45)' },
};

// 14px chevron tip; segments overlap by 12px so a 2px sliver of page
// background separates them even when neighbours share a color.
const CHEVRON_TIP = 14;
const chevronClip = (isFirst, isLast) => {
    const right = isLast
        ? '100% 0, 100% 100%'
        : `calc(100% - ${CHEVRON_TIP}px) 0, 100% 50%, calc(100% - ${CHEVRON_TIP}px) 100%`;
    const notch = isFirst ? '' : `, ${CHEVRON_TIP}px 50%`;
    return `polygon(0 0, ${right}, 0 100%${notch})`;
};

/** Chevron-segment progress bar: color = pipeline state, chip = human review state. */
const PipelineProgress = ({ item, activeStep, review, onSelect, onToggleApproved }) => (
    <div style={{ display: 'flex', marginBottom: 24 }}>
        {PIPELINE_STEPS.map((s, i) => {
            const state =
                s.step === item.currentStep ? 'current' : s.step < item.currentStep ? 'done' : 'future';
            const colors = STEP_COLORS[state];
            const viewed = s.step === activeStep;
            const r = review[s.step] || {};
            const isFirst = i === 0;
            const isLast = i === PIPELINE_STEPS.length - 1;
            return (
                <div
                    key={s.step}
                    onClick={() => onSelect(s.step)}
                    style={{
                        flex: 1,
                        minWidth: 0,
                        marginLeft: isFirst ? 0 : -(CHEVRON_TIP - 2),
                        padding: `10px ${CHEVRON_TIP + 8}px 10px ${isFirst ? 16 : CHEVRON_TIP + 14}px`,
                        cursor: 'pointer',
                        background: viewed ? colors.bgViewed : colors.bg,
                        clipPath: chevronClip(isFirst, isLast),
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6,
                    }}
                >
                    <span
                        style={{
                            fontWeight: 600,
                            fontSize: 13,
                            color: colors.title,
                            textDecoration: viewed ? 'underline' : 'none',
                        }}
                    >
                        {s.title}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        {state === 'current' && <CurrentBadge />}
                        {state === 'done' && <DoneBadge />}
                        {r.seen ? (
                            <ReviewChip
                                approved={!!r.approved}
                                onToggle={(e) => {
                                    e.stopPropagation();
                                    onToggleApproved(s.step);
                                }}
                            />
                        ) : (
                            <UnseenChip />
                        )}
                    </div>
                </div>
            );
        })}
    </div>
);

/** Clickable review chip: "Seen" ⇄ "Approved". */
const ReviewChip = ({ approved, onToggle }) => (
    <span
        onClick={onToggle}
        title={approved ? 'Click to disapprove' : 'Click to approve'}
        style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '1px 10px',
            borderRadius: 11,
            cursor: 'pointer',
            userSelect: 'none',
            fontSize: 12,
            fontWeight: 500,
            lineHeight: '18px',
            background: approved ? 'rgba(82,196,26,0.20)' : 'rgba(146,84,222,0.22)',
            color: approved ? '#73d13d' : '#b37feb',
        }}
    >
        {approved ? <CheckCircleFilled style={{ fontSize: 12 }} /> : <EyeOutlined style={{ fontSize: 12 }} />}
        {approved ? 'Approved' : 'Seen'}
    </span>
);

/**
 * The catalog matches from the post-extract duplicate check (linked to the
 * back-office item editor) and an approve button. While the check holds the
 * item (a complete extraction), approving resumes the AUTOMATIC flow from
 * assets — the hold forces manual mode; otherwise it just clears the warning.
 */
const DuplicateMatches = ({ item, held }) => {
    const [approving, setApproving] = useState(false);
    const check = item.duplicateCheck || {};

    const seen = new Set();
    const matches = [];
    // URL matches first — the stronger signal wins when an item matches both ways.
    for (const m of [
        ...(check.urlMatches || []).map((d) => ({ ...d, reason: 'same purchase URL' })),
        ...(check.nameMatches || []).map((d) => ({ ...d, reason: 'similar name in the same category' })),
    ]) {
        if (!seen.has(m.itemId)) {
            seen.add(m.itemId);
            matches.push(m);
        }
    }

    const approve = async () => {
        setApproving(true);
        try {
            await ingestionApi.approveDuplicates(item.id);
            message.success(held ? 'Approved — the item continues automatically' : 'Marked as new');
            await ingestionStore.loadItem(item.id);
        } catch (e) {
            message.error(e.message || 'Failed to approve');
        } finally {
            setApproving(false);
        }
    };

    return (
        <Space direction="vertical" size={8} style={{ marginTop: 8, width: '100%' }}>
            {matches.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {matches.map((m) => (
                        <li key={m.itemId}>
                            <a href={`/catalog/update/${m.itemId}`} target="_blank" rel="noreferrer">
                                {m.name || m.itemId}
                            </a>
                            <Typography.Text type="secondary"> — {m.reason}</Typography.Text>
                        </li>
                    ))}
                </ul>
            )}
            <Popconfirm
                title="Not a duplicate?"
                description={held ? 'The item will resume the automatic flow from Assets.' : 'The warning is cleared.'}
                okText={held ? 'Approve & continue' : 'Mark as new'}
                onConfirm={approve}
            >
                <Button type="primary" size="small" loading={approving}>
                    It&apos;s new{held ? ' — approve & continue' : ''}
                </Button>
            </Popconfirm>
        </Space>
    );
};

export const IngestionDetail = observer(() => {
    const { id } = useParams();
    const history = useHistory();
    const currentUserStore = useCurrentUser();
    const [viewStep, setViewStep] = useState(null);
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejecting, setRejecting] = useState(false);
    const [review, setReview] = useState({});
    // Bumped by the top-bar Refresh; every step reloads its own data on it.
    const [refreshTick, setRefreshTick] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const refresh = async () => {
        setRefreshing(true);
        try {
            await ingestionStore.refreshCurrentItem(id);
            setRefreshTick((t) => t + 1);
        } finally {
            setRefreshing(false);
        }
    };

    useEffect(() => {
        ingestionStore.loadItem(id);
        setViewStep(null);
        setReview(loadStepReview(id));
    }, [id]);

    const item = ingestionStore.currentItem;
    const activeStep = viewStep ?? item?.currentStep;

    // Displaying a step counts as a human having seen it.
    useEffect(() => {
        if (ingestionStore.loadingItem || !item || !activeStep) return;
        setReview((prev) => {
            if (prev[activeStep]?.seen) return prev;
            const next = { ...prev, [activeStep]: { ...prev[activeStep], seen: true } };
            saveStepReview(id, next);
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, item, activeStep, ingestionStore.loadingItem]);

    const toggleApproved = (step) => {
        setReview((prev) => {
            const wasApproved = !!prev[step]?.approved;
            const next = { ...prev, [step]: { ...prev[step], approved: !wasApproved, seen: true } };
            saveStepReview(id, next);
            return next;
        });
    };

    if (ingestionStore.loadingItem) {
        return <Spin style={{ display: 'block', margin: '80px auto' }} />;
    }
    if (ingestionStore.itemError) {
        return (
            <div style={{ padding: 24 }}>
                <Alert type="error" message={ingestionStore.itemError} showIcon />
            </div>
        );
    }
    if (!item) {
        return null;
    }

    const statusMeta = STATUS_LABELS[item.status] || { text: item.status, color: 'default' };
    const canReject = !!item.submittedByUid && item.status !== 'published' && item.status !== 'rejected'
        && currentUserStore.canOnItem('upload-pipeline.write', item);

    // stepRuns come newest-first; surface a message only if the most recent run
    // that reached a terminal outcome carries an error — so any later terminal run
    // without one (succeeded, or parked needs_input) clears a stale failure.
    // A `needs_input` run with a message is a soft hold (e.g. a duplicate flagged
    // after step 1), not a hard failure — show it as a warning.
    const TERMINAL_STATUSES = ['succeeded', 'failed', 'needs_input'];
    const lastTerminalRun = (item.stepRuns || []).find((r) => r.error || TERMINAL_STATUSES.includes(r.status));
    const flaggedRun = lastTerminalRun && lastTerminalRun.error ? lastTerminalRun : null;
    const flaggedStepTitle = flaggedRun
        ? (PIPELINE_STEPS.find((s) => s.step === flaggedRun.step) || {}).title || `Step ${flaggedRun.step}`
        : null;
    const flaggedIsReview = flaggedRun && flaggedRun.status === 'needs_input';
    // The post-extract duplicate check: unapproved matches show until an admin
    // marks the item new. While the check is what parked the item (extract, held),
    // its message rides on the flagged run; otherwise it's a standalone warning.
    const duplicateCheck = item.duplicateCheck;
    const hasDuplicates =
        !!duplicateCheck &&
        !duplicateCheck.approvedAt &&
        ((duplicateCheck.urlMatches || []).length > 0 || (duplicateCheck.nameMatches || []).length > 0);
    const duplicateHeld = hasDuplicates && duplicateCheck.held && item.currentStep === 3 && item.status === 'needs_input';
    const flaggedIsDuplicateHold = flaggedIsReview && flaggedRun.step === 3 && !!duplicateCheck?.held;

    const saveVariations = async (payload) => {
        try {
            await ingestionStore.updateVariations(item.id, payload);
            await ingestionStore.loadItem(item.id);
            message.success('Variations saved');
        } catch (e) {
            message.error(e.message || 'Failed to save variations');
        }
    };

    const handleReject = async (reason) => {
        setRejecting(true);
        try {
            await ingestionApi.rejectItem(item.id, reason);
            await ingestionStore.loadItem(item.id);
            setRejectOpen(false);
            message.success('Request rejected; the user has been notified');
        } catch (e) {
            message.error(e.message || 'Failed to reject the request');
        } finally {
            setRejecting(false);
        }
    };

    const handleDelete = async () => {
        try {
            await ingestionApi.deleteItem(item.id);
            message.success('Item deleted');
            history.push('/ingestion');
        } catch (e) {
            message.error(e.message || 'Failed to delete item');
        }
    };

    const handleStep1Submit = (payload) => {
        // Editing variations (e.g. a purchase URL) on an item that already moved
        // past step 1 rewinds it — make that explicit before saving.
        if (item.currentStep > 1) {
            Modal.confirm({
                title: 'Send item back to step 1?',
                content:
                    'Editing the variations (e.g. a purchase URL) will send the item back to step 1 and re-run scrape / extract / assets from there.',
                okText: 'Send back & save',
                onOk: () => saveVariations(payload),
            });
            return;
        }
        saveVariations(payload);
    };

    const renderStep = () => {
        if (activeStep === 1) {
            return <Step1Variations item={item} saving={ingestionStore.saving} onSubmit={handleStep1Submit} />;
        }
        if (activeStep === 2) {
            return <Step2Scrape item={item} refreshTick={refreshTick} />;
        }
        if (activeStep === 3) {
            return <Step3Extract item={item} refreshTick={refreshTick} />;
        }
        if (activeStep === 4) {
            return <Step4Assets item={item} refreshTick={refreshTick} />;
        }
        if (activeStep === 5) {
            return <Step5Compress item={item} refreshTick={refreshTick} />;
        }
        if (activeStep === 6) {
            return <Step6Confirm item={item} />;
        }
        return <PlaceholderStep step={activeStep} />;
    };

    return (
        <div style={{ padding: 24 }}>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
                <Space>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => history.push('/ingestion')}>
                        Back
                    </Button>
                    <Title level={3} style={{ margin: 0 }}>
                        {item.name}
                    </Title>
                    <Tag color={item.mode === 'auto' ? 'blue' : 'default'}>{item.mode}</Tag>
                    {MODEL_ITEM_TYPES.includes(item.itemType) && (
                        <Tag color={item.modelEngine === 'opus' ? 'geekblue' : 'default'}>
                            {item.modelEngine === 'opus' ? 'Opus builder' : 'Meshy'}
                        </Tag>
                    )}
                    <Tag color={statusMeta.color}>{statusMeta.text}</Tag>
                    {item.submittedByUid && (
                        <Tooltip
                            title={
                                `Requested by ${item.submittedByEmail || item.submittedByUid}. ` + (item.submissionCheck
                                    ? `Check ${item.submissionCheck.passed ? 'passed' : 'rejected'}: ${item.submissionCheck.note || item.submissionCheck.reason || ''}`
                                    : 'Moderation check pending (runs before extract)')
                            }
                        >
                            <Tag color="purple">User request{item.submittedByEmail ? ` · ${item.submittedByEmail}` : ''}</Tag>
                        </Tooltip>
                    )}
                </Space>
                <Space>
                    <Tooltip title="Reload the item and this step's data">
                        <Button icon={<ReloadOutlined />} onClick={refresh} loading={refreshing}>
                            Refresh
                        </Button>
                    </Tooltip>
                    {canReject && (
                        <Button icon={<StopOutlined />} onClick={() => setRejectOpen(true)}>
                            Reject request
                        </Button>
                    )}
                    {currentUserStore.canOnItem('upload-pipeline.delete', item) && (
                        <Popconfirm
                            title="Delete this item?"
                            description="This permanently removes the item and all its data."
                            okText="Delete"
                            okButtonProps={{ danger: true }}
                            onConfirm={handleDelete}
                        >
                            <Button danger icon={<DeleteOutlined />}>
                                Delete
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            </Space>
            {canReject && (
                <RejectRequestDialog open={rejectOpen} submitting={rejecting} onCancel={() => setRejectOpen(false)} onConfirm={handleReject} />
            )}

            {flaggedRun && !(flaggedIsDuplicateHold && !hasDuplicates) && (
                <Alert
                    type={flaggedIsReview ? 'warning' : 'error'}
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={flaggedIsReview ? `${flaggedStepTitle} needs review` : `${flaggedStepTitle} failed`}
                    description={
                        <>
                            {flaggedRun.error}
                            {flaggedIsDuplicateHold && hasDuplicates && <DuplicateMatches item={item} held={duplicateHeld} />}
                        </>
                    }
                />
            )}
            {hasDuplicates && !flaggedIsDuplicateHold && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="This item may already exist in the catalog"
                    description={<DuplicateMatches item={item} held={duplicateHeld} />}
                />
            )}

            <PipelineProgress
                item={item}
                activeStep={activeStep}
                review={review}
                onSelect={setViewStep}
                onToggleApproved={toggleApproved}
            />

            {renderStep()}
        </div>
    );
});
